import { useAtomValue, useSetAtom } from "jotai";
import { MemoTable } from "../../components/MemoTable";
import { PeriodSelectorMenu } from "@/features/Metrics/components/PeriodSelectorMenu";
import { AIMContextViewAtom } from "../../panels/AIMPanelContext";
import clsx from "clsx";
import {
    getTableDataAtom,
    metricsAtom,
} from "@/features/Metrics/state/Metrics.state";
import { MetricCodeWithFallbacks } from "@/features/Metrics/Metrics";
import { getPeriodsAtom, handleCellClickAtom } from "./SummaryFinancials.state";
import { HeaderWithTooltip } from "../../components/HeaderWithTooltip";
import { DeltaCellRenderer } from "./DeltaCellRenderer";
import {
    DeltaType,
    PeriodOption,
    PeriodResolution,
} from "@/features/Metrics/Metrics.types";
import { useState } from "react";
import { getPeriodOptions } from "@/features/Metrics/helpers/getPeriodThresholds";
import { ICellRendererParams } from "ag-grid-enterprise";
import { DeltaCalculator } from "@/features/Metrics/Delta";

export function SummaryTable({
    gridId,
    codes,
    title,
    deltaType = DeltaType.PERIOD_OVER_PERIOD,
}: {
    gridId: string;
    codes: MetricCodeWithFallbacks[];
    title: string;
    deltaType?: DeltaType;
}) {
    const [selectedOption, setSelectedOption] = useState<PeriodOption>(
        getPeriodOptions(PeriodResolution.QUARTERLY).find(
            (o) => o.id === "l4q",
        ) ?? getPeriodOptions(PeriodResolution.QUARTERLY)[0],
    );
    const { isLoading, data: metrics } = useAtomValue(metricsAtom);
    const AIMContextView = useAtomValue(AIMContextViewAtom);
    const selectedCell = (AIMContextView as any)?.selectedCell;
    const getHandleCellClick = useSetAtom(handleCellClickAtom);
    const getPeriods = useAtomValue(getPeriodsAtom);
    const periodRange = getPeriods(codes, selectedOption);
    const periodColumns = periodRange?.toColumns(selectedOption.resolution, {
        valueFormatter: (params) => params.value?.getFormattedValue() ?? "-",
    });
    const getData = useAtomValue(getTableDataAtom);
    const data = getData(codes, selectedOption.resolution);

    return (
        <div id={gridId.replace("-grid", "")} className="space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="heading-m">{title}</h4>
                <PeriodSelectorMenu
                    selectedOption={selectedOption}
                    onSelect={setSelectedOption}
                    allowedPeriodRange={
                        metrics?.getGlobalPeriodRange(
                            selectedOption.resolution,
                            codes,
                        ) ?? null
                    }
                />
            </div>
            {isLoading ? (
                <div
                    className={clsx(
                        "h-[400px] w-full animate-pulse rounded-lg bg-slate-600",
                    )}
                />
            ) : (
                <MemoTable
                    data={data}
                    onCellClicked={(params) =>
                        getHandleCellClick(params, deltaType, selectedOption)
                    }
                    gridId={gridId}
                    columns={[
                        {
                            field: "label",
                            headerName: "",
                            flex: 1.5,
                            pinned: "left",
                            cellClass: "heading-s",
                        },
                        ...(periodColumns ?? []),
                        {
                            field: "delta",
                            maxWidth: 150,
                            pinned: "right",
                            headerName:
                                deltaType === DeltaType.PERIOD_OVER_PERIOD &&
                                periodColumns &&
                                periodColumns.length >= 2
                                    ? `% Δ ${periodColumns[periodColumns.length - 2].headerName} — ${periodColumns[periodColumns.length - 1].headerName}`
                                    : "% Δ",
                            headerComponent: HeaderWithTooltip,
                            cellRenderer: (params: ICellRendererParams) => {
                                const deltaCalculator =
                                    params.data?.deltaCalculator;
                                const delta =
                                    deltaCalculator && deltaCalculator.getDelta
                                        ? deltaCalculator.getDelta(
                                              deltaType,
                                              selectedOption,
                                          )
                                        : DeltaCalculator.getEmptyDelta();
                                return DeltaCellRenderer({ delta });
                            },
                            headerComponentParams: {
                                tooltipText:
                                    "Shows the % difference between the latest two available periods. Eg. if the last closed period is Q1-25, the delta shows the difference between Q1-25 and Q4-24.",
                            },
                        },
                    ]}
                    defaultColDef={{
                        cellStyle: (params: any) => {
                            return {
                                paddingLeft:
                                    (params.data?.indentLevel ?? 0) * 8 + "px",
                                cursor: "pointer",
                            };
                        },
                        flex: 1,
                        minWidth: 140,
                        cellClass: (params) => {
                            const isSelected =
                                gridId === selectedCell?.gridId &&
                                selectedCell?.rowId === params.node.id &&
                                selectedCell?.colField === params.colDef.field;
                            return params.colDef.field !== "label"
                                ? `cursor-pointer hover:bg-hover ${isSelected ? "bg-selected" : ""}`
                                : "";
                        },
                    }}
                />
            )}
        </div>
    );
}
